import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sun, Cloud, CloudRain, CloudLightning, CloudFog, Wind, Droplets, Waves, Thermometer, CalendarHeart, MapPin, MessageCircle } from "lucide-react";
import { api, waLink } from "@/lib/api";
import FestivalPackageDialog from "@/components/FestivalPackageDialog";
import { ShareButton } from "@/components/Logo";

const wmo = (code) => {
  if (code === 0) return { label: "Clear sky", Icon: Sun };
  if (code <= 2) return { label: "Partly cloudy", Icon: Cloud };
  if (code === 3) return { label: "Overcast", Icon: Cloud };
  if (code <= 48) return { label: "Foggy", Icon: CloudFog };
  if (code <= 67) return { label: "Rain", Icon: CloudRain };
  if (code <= 82) return { label: "Showers", Icon: CloudRain };
  return { label: "Thunderstorm", Icon: CloudLightning };
};
const seaState = (h) => (h == null ? "" : h < 0.5 ? "Calm sea · great for swimming" : h < 1.25 ? "Gentle waves · swim in lifeguard zone" : h < 2.5 ? "Moderate waves · caution advised" : "Rough sea · no swimming");
const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
const daysUntil = (d) => Math.ceil((new Date(`${d}T00:00:00`) - new Date().setHours(0, 0, 0, 0)) / 86400000);
const CAT = { religious: "bg-terra/15 text-terra", cultural: "bg-foam/20 text-ink", seasonal: "bg-gold/25 text-ink" };

function WeatherCard({ w }) {
  if (!w) return <div className="bg-white/[0.06] border border-sand/10 rounded-3xl p-6 text-sand/50 text-sm" data-testid="weather-loading">Fetching Puri weather…</div>;
  const cur = wmo(w.current.weather_code);
  return (
    <div className="bg-white/[0.06] border border-sand/10 rounded-3xl p-6 sm:p-7" data-testid="weather-card">
      <p className="font-mono text-[11px] uppercase tracking-widest text-gold flex items-center gap-2"><MapPin size={12} /> Puri right now</p>
      <div className="mt-4 flex items-center gap-5">
        <cur.Icon size={56} className="text-gold shrink-0" strokeWidth={1.5} />
        <div>
          <p className="font-serif text-5xl font-black leading-none" data-testid="weather-temp">{Math.round(w.current.temperature_2m)}°<span className="text-2xl">C</span></p>
          <p className="text-sm text-sand/70 mt-1" data-testid="weather-label">{cur.label} · feels like {Math.round(w.current.apparent_temperature)}°</p>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-2 text-xs">
        <span className="flex items-center gap-1.5 text-sand/70"><Droplets size={13} className="text-foam" /> {w.current.relative_humidity_2m}%</span>
        <span className="flex items-center gap-1.5 text-sand/70"><Wind size={13} className="text-foam" /> {Math.round(w.current.wind_speed_10m)} km/h</span>
        {w.marine?.sea_surface_temperature != null && <span className="flex items-center gap-1.5 text-sand/70"><Thermometer size={13} className="text-foam" /> Sea {Math.round(w.marine.sea_surface_temperature)}°</span>}
      </div>
      {w.marine?.wave_height != null && (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-gold" data-testid="weather-sea"><Waves size={13} /> {seaState(w.marine.wave_height)} ({w.marine.wave_height} m)</p>
      )}
      <div className="mt-5 grid grid-cols-3 gap-2" data-testid="weather-forecast">
        {w.daily.time.slice(1, 4).map((t, i) => {
          const d = wmo(w.daily.weather_code[i + 1]);
          return (
            <div key={t} className="bg-ink/40 rounded-xl p-2.5 text-center">
              <p className="text-[10px] font-mono uppercase text-sand/50">{new Date(`${t}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short" })}</p>
              <d.Icon size={18} className="mx-auto my-1 text-sand/80" />
              <p className="text-xs font-semibold">{Math.round(w.daily.temperature_2m_max[i + 1])}° <span className="text-sand/50">{Math.round(w.daily.temperature_2m_min[i + 1])}°</span></p>
              {w.daily.precipitation_probability_max[i + 1] > 30 && <p className="text-[10px] text-foam">{w.daily.precipitation_probability_max[i + 1]}% rain</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function LivePuri() {
  const [weather, setWeather] = useState(null);
  const [festivals, setFestivals] = useState([]);
  const [pkg, setPkg] = useState(null);
  useEffect(() => {
    api.get("/weather").then((r) => setWeather(r.data)).catch(() => {});
    api.get("/festivals?limit=5").then((r) => setFestivals(r.data)).catch(() => {});
  }, []);

  return (
    <section id="live" className="py-16 lg:py-20 bg-ink text-sand relative overflow-hidden" data-testid="live-puri-section">
      <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-gold/10 blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }} className="lg:col-span-4">
          <WeatherCard w={weather} />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, delay: 0.1 }} className="lg:col-span-8">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold flex items-center gap-2 mb-3"><CalendarHeart size={12} /> Coming up in Odisha</p>
          <h2 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight mb-5">Plan around a <span className="italic text-terra">festival</span></h2>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="festival-list">
            {festivals.length === 0 && <p className="text-sm text-sand/50">Festival calendar coming soon.</p>}
            {festivals.map((f) => {
              const n = daysUntil(f.start_date);
              return (
                <div key={f.id} data-testid={`festival-${f.id.slice(0, 8)}`}
                  className="group bg-white/[0.06] hover:bg-white/[0.1] border border-sand/10 rounded-2xl p-4 flex gap-4 transition-colors">
                  <div className="shrink-0 w-14 text-center bg-sand text-ink rounded-xl py-2 self-start">
                    <p className="font-serif text-2xl font-black leading-none">{new Date(`${f.start_date}T00:00:00`).getDate()}</p>
                    <p className="text-[10px] font-mono uppercase">{new Date(`${f.start_date}T00:00:00`).toLocaleDateString("en-IN", { month: "short" })}</p>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold leading-snug">{f.name}</p>
                      <span className={`shrink-0 text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${CAT[f.category] || CAT.religious}`}>{f.category}</span>
                    </div>
                    <p className="text-[11px] text-sand/50 mt-0.5">{f.place} · {n <= 0 ? "Happening now" : `in ${n} day${n > 1 ? "s" : ""}`}{f.approximate && " · dates approx."}</p>
                    <p className="text-xs text-sand/70 mt-1.5 leading-relaxed line-clamp-2">{f.description}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {f.package_enabled && (
                        <button onClick={() => setPkg(f)} data-testid={`festival-package-button-${f.id.slice(0, 8)}`}
                          className="text-xs font-semibold bg-terra text-sand px-3.5 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors">
                          Package from ₹{Number(f.package_price).toLocaleString("en-IN")}
                        </button>
                      )}
                      <a href={waLink(`Hi Random Rider! I'd like to plan a trip around ${f.name} (${fmt(f.start_date)}${f.end_date && f.end_date !== f.start_date ? ` – ${fmt(f.end_date)}` : ""}). Can you suggest a package?`)}
                        target="_blank" rel="noreferrer" data-testid={`festival-whatsapp-${f.id.slice(0, 8)}`}
                        className="text-[11px] text-gold flex items-center gap-1 hover:underline"><MessageCircle size={11} /> Ask on WhatsApp</a>
                      <ShareButton kind="festivals" slug={f.id} title={f.name} label="Share" testId={`festival-share-${f.id.slice(0, 8)}`} className="text-[11px] text-sand/60 hover:text-gold" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
      <FestivalPackageDialog festival={pkg} onClose={() => setPkg(null)} />
    </section>
  );
}
